import React, { useEffect } from "react";
import { Alert, Spinner } from "react-bootstrap";
import RIcon from "../reservas/components/ReservasIcons";
import { BEAUTY_FALLBACK_IMAGE, HoldCountdown, resolveUrl } from "../reservas/components/ReservaShared";
import StepIndicator from "./components/StepIndicator";
import {
  AppointmentDetails,
  AsideAmounts,
  ConsentCheck,
  ContactFields,
  DayPicker,
  HoldAmounts,
  LinkedProfessionalCard,
  PaymentModal,
  PaymentNotice,
  PolicyBox,
  ProfessionalNote,
  ProfessionalOptions,
  SlotPicker,
  SummaryRows,
} from "./components/BookingParts";
import { formatDuration, formatLongDate } from "./beautyBooking";
import { claveDeLinea, nombreDeLinea } from "../../helpers/beautyLineas";
import "../reservas/reservas.css";
import "./beauty.css";

export const DESKTOP_STEP_TURN = 2;
export const DESKTOP_STEP_DATA = 3;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/**
 * Desktop booking steps 2 ("Elegir turno") and 3 ("Tus datos" + payment). Step 1
 * is the salon page itself. Same structure as the Canchas STEP_SLOTS and
 * STEP_FORM views. State and API calls live in `useBeautyBookingFlow`.
 */
export default function BeautyBookingDesktop({ salon, flow, step, onStepChange }) {
  const {
    cartServices,
    effectiveTotals,
    holdTotals,
    availability,
    selectedSlot,
    hold,
    holdLoading,
    holdError,
    holdExpired,
    date,
    professionalId,
    selectedProfessional,
  } = flow;

  // A hold error belongs to the visit that produced it: drop it on leaving.
  const { clearHoldError } = flow;
  useEffect(() => () => clearHoldError(), [clearHoldError]);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.scrollTo === "function" && process.env.NODE_ENV !== "test") {
      window.scrollTo(0, 0);
    }
  }, [step]);

  const windowDays = Number(salon.reservationWindowDays);
  const professionalLabel = selectedProfessional ? selectedProfessional.name : "Sin preferencia";
  const durationLabel = formatDuration(effectiveTotals.durationMinutes);
  const hasDeposit = Number(holdTotals?.deposit) > 0;
  const slotEnd = flow.slotEndFor(selectedSlot);
  const timeRange = selectedSlot ? (slotEnd ? `${selectedSlot.slotStart} a ${slotEnd} hs` : `${selectedSlot.slotStart} hs`) : "";
  const img = resolveUrl(salon.image || salon.mobileimage) || BEAUTY_FALLBACK_IMAGE;

  const { linked } = flow;

  // ---- Step 2: Elegir turno ----
  const renderTurn = () => {
    const noneEligible = flow.professionalOptions.length > 0 && flow.professionalOptions.every((o) => !o.eligible);
    const isAny = availability.data?.mode === "any";
    const slotPrice = selectedSlot
      ? (isAny ? Number(selectedSlot.priceFrom) : Number(availability.data?.totalPrice))
      : effectiveTotals.price;
    const slotDeposit = selectedSlot
      ? (isAny ? Number(selectedSlot.depositFrom) : Number(availability.data?.totalDeposit))
      : effectiveTotals.deposit;
    const showFrom = selectedSlot ? isAny : effectiveTotals.from;

    const leave = (target) => {
      flow.clearHoldError();
      onStepChange(target);
    };

    const onContinue = async () => {
      const result = await flow.createHold();
      if (result === "created") onStepChange(DESKTOP_STEP_DATA);
    };

    return (
      <div className="wrap bar-detail beauty-turn">
        <button type="button" className="crumb" onClick={() => leave(1)}>
          <RIcon name="chevL" width={16} height={16} /> Volver a los servicios
        </button>

        <StepIndicator current={DESKTOP_STEP_TURN} />

        <div className="bd-grid">
          <div className="beauty-main">
            {linked ? (
              <div id="beauty-pro-card"><LinkedProfessionalCard flow={flow} /></div>
            ) : (
            <div className="bd-section beauty-pick-card beauty-pro-card" id="beauty-pro-card">
              <div className="beauty-section-head">
                <h3 className="section-title">¿Con quién te atendés?</h3>
                <span className="beauty-soft-pill">Opcional</span>
              </div>
              <ProfessionalOptions flow={flow} desktop />
              {noneEligible && (
                <Alert variant="light" className="mt-3">
                  <p>Ningún profesional hace todos los servicios de tu turno. Podés dejar Sin preferencia o cambiar tu selección.</p>
                  <button type="button" className="r-btn-soft" onClick={() => leave(1)}>Modificar servicios</button>
                </Alert>
              )}
              <ProfessionalNote />
            </div>
            )}

            <div className="beauty-pick-row">
              <div className="bd-section beauty-pick-card beauty-day-card" id="beauty-date-card">
                <h3 className="section-title">¿Qué día?</h3>
                <DayPicker flow={flow} salon={salon} desktop />
                {Number.isFinite(windowDays) && windowDays > 0 && (
                  <p className="beauty-footnote">Los turnos se pueden reservar hasta con {windowDays} días de antelación.</p>
                )}
              </div>

              <div className="bd-section beauty-pick-card">
                <h3 className="section-title">¿A qué hora?</h3>
                {!date ? (
                  <p className="beauty-step-text">Elegí un día para ver los horarios disponibles.</p>
                ) : (
                  <>
                    <p className="beauty-selected-date">{formatLongDate(date)}</p>
                    <p className="beauty-info-note">
                      <RIcon name="info" width={15} height={15} />
                      Te mostramos solo los horarios en los que entran tus servicios ({durationLabel}).
                    </p>
                    {holdError && <Alert variant="warning">{holdError}</Alert>}
                    <div className="beauty-slots-scroll">
                      <SlotPicker
                        flow={flow}
                        desktop
                        onChangeDate={() => scrollToId("beauty-date-card")}
                        onChangeProfessional={linked ? linked.leave : () => scrollToId("beauty-pro-card")}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <aside className="bd-sidebar-sticky" aria-label="Resumen">
            <div className="reserve-stick">
              <div className="rs-head"><div className="t">Tu turno</div><div className="n">{salon.name}</div></div>
              <div className="rs-body">
                {!selectedSlot ? (
                  <div className="rs-empty">
                    <RIcon name="cal" width={34} height={34} />
                    <br />
                    Elegí un día y un horario para ver el resumen de tu turno.
                  </div>
                ) : (
                  <>
                    <SummaryRows
                      services={flow.cartLines.map((l, i) => ({ key: claveDeLinea(l, i), name: nombreDeLinea(l), price: l.price }))}
                      durationLabel={durationLabel}
                      professionalLabel={professionalLabel}
                      date={date}
                      timeRange={timeRange}
                    />
                    <AsideAmounts total={slotPrice} deposit={slotDeposit} from={showFrom} />
                  </>
                )}
                <button
                  type="button"
                  className="r-btn-primary rs-cta"
                  disabled={!selectedSlot || holdLoading}
                  onClick={onContinue}
                >
                  {holdLoading ? <Spinner animation="border" size="sm" /> : <>Continuar <RIcon name="arrowR" width={17} height={17} /></>}
                </button>
              </div>
            </div>
            <PolicyBox salon={salon} />
          </aside>
        </div>
      </div>
    );
  };

  // ---- Step 3: Tus datos ----
  const renderData = () => {
    const crumb = (
      <button type="button" className="crumb" onClick={() => onStepChange(DESKTOP_STEP_TURN)}>
        <RIcon name="chevL" width={16} height={16} /> Volver a elegir turno
      </button>
    );

    if (!hold || !holdTotals) {
      return (
        <div className="wrap reservar">
          {crumb}
          <StepIndicator current={DESKTOP_STEP_DATA} />
          <div className="beauty-expired">
            <RIcon name="clock" width={18} height={18} />
            <span>{holdExpired ? "El bloqueo expiró. Elegí un horario nuevamente." : "Todavía no elegiste un horario."}</span>
            <button
              type="button"
              className="r-btn-primary"
              onClick={() => { flow.dismissExpired(); onStepChange(DESKTOP_STEP_TURN); }}
            >
              Elegir horario
            </button>
            {holdError && <Alert variant="warning">{holdError}</Alert>}
          </div>
        </div>
      );
    }

    const holdProfessional = hold.professional?.name || professionalLabel;
    const holdRange = hold.slotEnd ? `${hold.slotStart} a ${hold.slotEnd} hs` : `${hold.slotStart} hs`;
    const rowProps = { holdTotals, date, professionalLabel, professionalId };

    const onSubmit = async (values) => {
      const result = await flow.onSubmit(values);
      if (result === "slots") onStepChange(DESKTOP_STEP_TURN);
    };

    return (
      <div className="wrap reservar">
        {crumb}
        <StepIndicator current={flow.showPaymentModal ? 4 : DESKTOP_STEP_DATA} />
        <HoldCountdown expiresAt={hold.expiresAt} onExpired={flow.handleHoldExpired} label="Tu turno está reservado por" />

        <div className="reservar-grid">
          <div className="form-card">
            <h3>Completá tus datos</h3>
            <p className="fc-sub">Usamos estos datos para confirmar tu reserva y enviarte el comprobante.</p>
            <form id="beauty-booking-form" onSubmit={flow.handleSubmit(onSubmit)} noValidate>
              <ContactFields flow={flow} desktop />
            </form>

            <AppointmentDetails hold={hold} desktop {...rowProps} />
            <PaymentNotice hasDeposit={hasDeposit} salon={salon} />
            <ConsentCheck flow={flow} salon={salon} />
            {flow.submitError && <Alert variant="danger" className="mt-3">{flow.submitError}</Alert>}

            <div className="beauty-form-actions">
              <button type="button" className="r-btn-soft" onClick={() => onStepChange(DESKTOP_STEP_TURN)}>Volver</button>
              <button
                type="submit"
                form="beauty-booking-form"
                className="r-btn-primary"
                disabled={!flow.consent || flow.submitting}
              >
                {flow.submitting
                  ? <Spinner animation="border" size="sm" />
                  : <>{hasDeposit ? "Ir al pago" : "Confirmar reserva"} <RIcon name="arrowR" width={17} height={17} /></>}
              </button>
            </div>

            <div className="beauty-form-secure">
              <RIcon name="shield" width={15} height={15} />
              {hasDeposit
                ? "Te vamos a redirigir a nuestro sistema de pago seguro para confirmar la seña."
                : "Al confirmar, la reserva se procesará automáticamente. No habrá necesidad de cargar un método de pago."}
            </div>
          </div>

          <aside className="sum-card" aria-label="Resumen">
            <div className="sum-img">
              <img src={img} alt={salon.name} onError={(e) => { e.currentTarget.src = BEAUTY_FALLBACK_IMAGE; }} />
              <div className="sc" />
              <div className="bn">{salon.name}</div>
            </div>
            <div className="sum-body">
              <h4>Resumen</h4>
              <SummaryRows
                services={hold.services.map((s, i) => ({ key: claveDeLinea(s, i), name: nombreDeLinea(s), sub: formatDuration(s.durationMinutes), price: s.price }))}
                durationLabel={formatDuration(holdTotals.durationMinutes)}
                professionalLabel={holdProfessional}
                date={date}
                timeRange={holdRange}
              />
              <HoldAmounts holdTotals={holdTotals} hasDeposit={hasDeposit} desktop />
            </div>
          </aside>
        </div>
        <PaymentModal flow={flow} holdTotals={holdTotals} />
      </div>
    );
  };

  return (
    <div className="reservas-root beauty-desktop">
      {step === DESKTOP_STEP_DATA ? renderData() : renderTurn()}
    </div>
  );
}
